import { cn } from '@plato/ui/util/classes';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideProps } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

const buttonVariants = cva(
    [
        'rounded-lg font-normal whitespace-nowrap',
        'inline-flex shrink-0 items-center justify-center gap-2',
        'ring-offset-background ring-offset-1 focus-visible:outline-hidden active:ring-1',
        'disabled:pointer-events-none disabled:opacity-50',
    ],
    {
        variants: {
            variant: {
                // old variants
                header: 'text-neutral-700 rounded-md border border-blue-300 bg-neutral-50 hover:bg-neutral-100 ',
                // new variants
                primary: [
                    'bg-blue-500 text-white',
                    'hover:bg-blue-700',
                    'active:bg-blue-700 active:ring-blue-200',
                    'aria-selected:bg-blue-600',
                ],
                secondary: [
                    'bg-blue-50 text-blue-700',
                    'hover:bg-blue-100 hover:text-blue-800',
                    'active:bg-blue-200 active:ring-blue-100',
                    'aria-selected:bg-blue-100',
                ],
                'secondary-ai': [
                    // We need to implement this variant with gradients
                    'bg-blue-50 text-neutral-950',
                    'hover:bg-blue-100 hover:text-blue-800',
                    'active:bg-blue-200 active:ring-blue-100',
                    'aria-selected:bg-blue-100',
                ],
                subtle: [
                    'bg-neutral-50 text-neutral-950',
                    'hover:bg-neutral-100',
                    'active:bg-neutral-200 active:ring-neutral-200',
                    'aria-selected:bg-gray-100',
                ],
                'subtle-ai': [
                    'border border-blue-300',
                    'bg-neutral-50 text-neutral-950',
                    'hover:bg-neutral-100',
                    'active:bg-neutral-200 active:ring-neutral-200',
                    'aria-selected:bg-gray-100',
                ],
                danger: [
                    'bg-red-500 text-white',
                    'hover:bg-red-600',
                    'active:bg-red-700 active:ring-red-200',
                    'aria-selected:bg-red-600',
                ],
                link: [
                    'bg-transparent text-blue-600',
                    'hover:text-blue-700 hover:underline hover:underline-offset-4',
                    'active:text-blue-700 active:underline active:underline-offset-4 active:ring-transparent',
                ],
                'link-danger': [
                    'bg-transparent text-red-500',
                    'hover:text-red-700 hover:underline hover:underline-offset-4',
                    'active:text-red-700 active:underline active:underline-offset-4 active:ring-transparent',
                    'aria-selected:text-red-600',
                ],
                icon: ['bg-neutral-50', 'hover:bg-neutral-100', 'active:bg-neutral-100 active:ring-gray-200'],
                outline: [
                    'border bg-white text-neutral-950 ring-neutral-200 ring-offset-0',
                    'hover:border-neutral-400',
                    'active:border-blue-500 active:text-neutral-950 active:ring-blue-200',
                    'aria-selected:text-neutral-950 aria-selected:ring-blue-200 aria-selected:border-blue-500',
                    'data-[state=open]:text-neutral-950 data-[state=open]:ring-blue-200 data-[state=open]:border-blue-500',
                ],
                'quick-filter': [
                    'rounded-full bg-neutral-25 text-neutral-950',
                    'ring-neutral-200 border ring-offset-0',
                    'hover:bg-neutral-50 hover:border-neutral-400',
                    'active:border-blue-500 active:bg-neutral-25 active:ring-blue-200 active:text-blue-600',
                    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                    'aria-selected:border-blue-600 aria-selected:bg-blue-50 aria-selected:text-blue-600',
                ],
                ghost: [
                    'bg-transparent text-neutral-950',
                    'hover:bg-neutral-100 data-[state=open]:bg-transparent',
                    'active:ring-transparent active:bg-neutral-200',
                    'aria-selected:bg-neutral-100',
                ],
                'select-ghost': [
                    'bg-white text-neutral-950 border border-transparent',
                    'hover:border-neutral-200',
                    'active:border-blue-500 active:text-neutral-950 active:ring-blue-200',
                    'data-[state=open]:text-neutral-950 data-[state=open]:border-blue-500',
                ],
                card: [
                    'bg-blue-25 text-blue-700',
                    'hover:bg-blue-100',
                    'active:bg-blue-600 active:text-white active:ring-blue-200',
                    'aria-selected:bg-blue-600 aria-selected:text-white',
                ],
            },
            size: {
                default: 'h-8 min-w-8 px-3 py-2.5 text-sm',
                sm: 'h-8 rounded-md px-2 text-sm',
                lg: 'h-10 rounded-md px-8',
                icon: 'h-8 w-8 p-2',
                'icon-s': 'h-8 w-8',
            },
            rounded: {
                true: 'rounded-full',
                md: 'rounded-md',
                lg: 'rounded-lg',
                xl: 'rounded-xl',
                '2xl': 'rounded-2xl',
                '3xl': 'rounded-3xl',
            },
            selected: {
                false: null,
                true: [],
            },
        },
        defaultVariants: {
            variant: 'secondary',
            size: 'default',
        },
        compoundVariants: [
            {
                variant: 'icon',
                size: 'default',
                className: ['h-8 w-8 p-2'],
            },
            {
                variant: 'card',
                size: 'default',
                className: ['h-5 w-5 min-w-5 p-0', 'rounded-full'],
            },
            {
                variant: 'link',
                size: 'default',
                className: ['p-0'],
            },
        ],
    },
);

type ButtonIconProps = {
    rotateOnActive?: boolean;
};

export type ButtonProps = {
    asChild?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    icon?: React.ComponentType<LucideProps>;
    leftIcon?: React.ComponentType<LucideProps>;
    leftIconProps?: ButtonIconProps;
    rightIcon?: React.ComponentType<LucideProps>;
    rightIconProps?: ButtonIconProps;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            rounded,
            selected,
            isDisabled,
            isLoading,
            asChild,
            children,
            icon: IconComponent,
            leftIcon: LeftIconComponent,
            leftIconProps,
            rightIcon: RightIconComponent,
            rightIconProps,
            ...props
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const Comp = asChild ? Slot : 'button';

        const combinedClassName = cn(
            'group',
            buttonVariants({ variant, size, rounded, selected, className }),
        );

        return (
            <Comp
                className={combinedClassName}
                ref={ref}
                disabled={isDisabled || isLoading}
                type="button"
                data-variant={variant}
                data-selected={Boolean(selected)}
                aria-selected={Boolean(selected)}
                aria-disabled={isDisabled || isLoading}
                {...props}
            >
                {isLoading ? (
                    `${t('loading')}...`
                ) : (
                    <>
                        {IconComponent && <IconComponent className="h-4 w-4 shrink-0" />}
                        {LeftIconComponent && (
                            <LeftIconComponent
                                className={cn('h-4 w-4 shrink-0', {
                                    'group-data-[state=open]:rotate-180': leftIconProps?.rotateOnActive,
                                })}
                            />
                        )}
                        {children}
                        {RightIconComponent && (
                            <RightIconComponent
                                className={cn('h-4 w-4 shrink-0', {
                                    'group-data-[state=open]:rotate-180': rightIconProps?.rotateOnActive,
                                })}
                            />
                        )}
                    </>
                )}
            </Comp>
        );
    },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
